/**
 * Optional Extras shares Minimog's native AJAX cart request and cart drawer.
 * Extra products are added as line items only when their quantity is greater
 * than zero. The displayed product price is the selected variant price plus
 * the current Optional Extras total.
 */
(() => {
  const money = (cents, format) => {
    if (typeof window.formatMoney === 'function') return window.formatMoney(cents, format);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  };

  const getExtrasScope = (productForm) => {
    const productInfo = productForm?.closest('product-info');
    return productInfo?.querySelector('[data-optional-extras]') || document.querySelector('product-info [data-optional-extras]');
  };

  const getExtraItems = (scope) => Array.from(scope.querySelectorAll('[data-optional-extra]'))
    .map((item) => {
      const quantityInput = item.querySelector('[data-optional-extra-quantity]');
      const quantity = Math.max(0, Number.parseInt(quantityInput.value, 10) || 0);

      return {
        item,
        quantity,
        variantId: item.dataset.optionalExtraVariantId,
        price: Number.parseInt(item.dataset.optionalExtraPrice, 10) || 0,
        title: item.dataset.optionalExtraTitle,
      };
    })
    .filter((item) => item.quantity > 0 && item.variantId);

  const updateDisplayedPrice = (scope, extrasTotal) => {
    const productInfo = scope.closest('product-info');
    const priceContainer = productInfo?.querySelector(`#Price-${productInfo.dataset.sectionId}`);
    if (!priceContainer) return;

    const basePrice = Number.parseInt(scope.dataset.optionalExtrasMainPrice, 10) || 0;
    const priceElement = priceContainer.querySelector(
      priceContainer.querySelector('.m-price--on-sale') ? '.m-price-item--sale' : '.m-price-item--regular'
    );

    if (priceElement) priceElement.textContent = money(basePrice + extrasTotal, scope.dataset.optionalExtrasMoneyFormat);
  };

  const renderSummary = (scope) => {
    const items = getExtraItems(scope);
    const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const summary = scope.querySelector('[data-optional-extras-summary]');
    const totalElement = scope.querySelector('[data-optional-extras-total]');
    const grandTotalElement = scope.querySelector('[data-optional-extras-grand-total]');
    const summaryItems = scope.querySelector('[data-optional-extras-summary-items]');
    const format = scope.dataset.optionalExtrasMoneyFormat;
    const mainProductPrice = Number.parseInt(scope.dataset.optionalExtrasMainPrice, 10) || 0;

    summary.hidden = items.length === 0;
    totalElement.textContent = money(total, format);
    grandTotalElement.textContent = money(mainProductPrice + total, format);
    summaryItems.replaceChildren();

    items.forEach((item) => {
      const row = document.createElement('li');
      row.className = 'm-optional-extras__summary-item';

      const title = document.createElement('span');
      title.textContent = `${item.quantity} × ${item.title}`;

      const price = document.createElement('span');
      price.className = 'm-optional-extras__summary-item-price';
      price.textContent = money(item.price * item.quantity, format);

      row.append(title, price);
      summaryItems.append(row);
    });

    updateDisplayedPrice(scope, total);
  };

  const setQuantity = (item, quantity) => {
    const input = item.querySelector('[data-optional-extra-quantity]');
    const decreaseButton = item.querySelector('[data-optional-extra-decrease]');
    const normalizedQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);

    input.value = normalizedQuantity;
    input.setAttribute('value', normalizedQuantity);
    decreaseButton.disabled = normalizedQuantity === 0;
    item.classList.toggle('m-optional-extras__product--selected', normalizedQuantity > 0);
    renderSummary(item.closest('[data-optional-extras]'));
  };

  const initialiseOptionalExtras = () => {
    document.querySelectorAll('[data-optional-extras]').forEach((scope) => {
      if (scope.dataset.optionalExtrasInitialised) return;
      scope.dataset.optionalExtrasInitialised = 'true';

      scope.addEventListener('click', (event) => {
        const control = event.target.closest('[data-optional-extra-increase], [data-optional-extra-decrease]');
        if (!control) return;

        const item = control.closest('[data-optional-extra]');
        const input = item.querySelector('[data-optional-extra-quantity]');
        const currentQuantity = Number.parseInt(input.value, 10) || 0;
        setQuantity(item, currentQuantity + (control.hasAttribute('data-optional-extra-increase') ? 1 : -1));
      });

      scope.addEventListener('change', (event) => {
        if (event.target.matches('[data-optional-extra-quantity]')) {
          setQuantity(event.target.closest('[data-optional-extra]'), event.target.value);
        }
      });

      scope.querySelector('[data-optional-extras-toggle]').addEventListener('click', (event) => {
        const button = event.currentTarget;
        const list = scope.querySelector('[data-optional-extras-summary-items]');
        const expanded = button.getAttribute('aria-expanded') === 'true';

        button.setAttribute('aria-expanded', String(!expanded));
        button.textContent = expanded ? 'See all' : 'Show less';
        list.hidden = expanded;
      });

      renderSummary(scope);
    });
  };

  const patchProductForm = () => {
    const ProductForm = customElements.get('product-form');
    if (!ProductForm || ProductForm.prototype.optionalExtrasPatched) return;

    const originalGetFormData = ProductForm.prototype.getFormData;

    ProductForm.prototype.getFormData = function getFormDataWithOptionalExtras() {
      const formData = originalGetFormData.call(this);
      const scope = getExtrasScope(this);
      const extraItems = scope ? getExtraItems(scope) : [];

      if (!extraItems.length) return formData;

      const itemsFormData = new FormData();
      const mainVariantId = formData.get('id');
      const mainQuantity = formData.get('quantity') || 1;

      if (!mainVariantId) return formData;

      itemsFormData.append('items[0][id]', mainVariantId);
      itemsFormData.append('items[0][quantity]', mainQuantity);

      for (const [name, value] of formData.entries()) {
        if (name === 'id' || name === 'quantity') continue;

        if (name.startsWith('properties[')) {
          itemsFormData.append(`items[0][properties][${name.slice(11, -1)}]`, value);
        } else if (name === 'selling_plan') {
          itemsFormData.append('items[0][selling_plan]', value);
        } else {
          itemsFormData.append(`items[0][${name}]`, value);
        }
      }

      extraItems.forEach((item, index) => {
        const itemIndex = index + 1;
        itemsFormData.append(`items[${itemIndex}][id]`, item.variantId);
        itemsFormData.append(`items[${itemIndex}][quantity]`, item.quantity);
      });

      return itemsFormData;
    };

    ProductForm.prototype.optionalExtrasPatched = true;
  };

  document.addEventListener('variant:changed', (event) => {
    document.querySelectorAll('[data-optional-extras]').forEach((scope) => {
      scope.dataset.optionalExtrasMainPrice = event.detail.variant.price;
      renderSummary(scope);
    });
  });

  const initialise = () => {
    initialiseOptionalExtras();
    customElements.whenDefined('product-form').then(patchProductForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
